import Phaser from 'phaser'
import { GameLogic, getPiecePositions, PieceState } from '@shared/game/GameLogic'
import { Board } from '@shared/game/Board'
import {
  BOARD_COLS, BOARD_ROWS,
  BLOCK_COLOR, BLOCK_DARK,
  CELL_EMPTY,
  SPEEDS,
  DAS_DELAY, DAS_REPEAT,
} from '@shared/constants'
import { SoundManager } from '../sound/SoundManager'
import { TouchController, isTouchDevice } from '../input/TouchController'
import { loadSettings } from '../data/settings'

// ─── Layout constants ──────────────────────────────────────────────────────────
const BS   = 46      // block size in pixels
const BX   = 80      // board left edge X
const BY   = 36      // board top edge Y

const PANEL_X = BX + BOARD_COLS * BS + 40   // right panel start

// ─── GameScene ────────────────────────────────────────────────────────────────

export class GameScene extends Phaser.Scene {
  private logic!: GameLogic
  private sound = new SoundManager()

  // Graphics layer (redrawn every frame)
  private gfx!: Phaser.GameObjects.Graphics

  // Text objects
  private txtScore!:  Phaser.GameObjects.Text
  private txtChain!:  Phaser.GameObjects.Text
  private txtNext!:   Phaser.GameObjects.Text
  private txtState!:  Phaser.GameObjects.Text
  private txtGarbage!: Phaser.GameObjects.Text
  private txtHelp!:   Phaser.GameObjects.Text

  // Input
  private keyLeft!:   Phaser.Input.Keyboard.Key
  private keyRight!:  Phaser.Input.Keyboard.Key
  private keyDown!:   Phaser.Input.Keyboard.Key

  private dasTimer:   number = 0
  private dasRepeat:  number = 0
  private dasActive:  boolean = false

  private touch: TouchController | null = null
  private lastSpawnCount = 0

  private prevPhase:       string  = ''
  private savedFlashCount: number  = 0
  private gameOverPlayed:  boolean = false

  constructor() {
    super({ key: 'GameScene' })
  }

  create(): void {
    this.logic = new GameLogic(Date.now() | 0, SPEEDS.normal)

    this.gfx = this.add.graphics()

    // ── Text objects ──────────────────────────────────────────────────────────
    this.txtScore = this.add.text(PANEL_X, 40, 'SCORE\n0', {
      fontFamily: 'monospace',
      fontSize: '20px',
      color: '#e0e0ff',
      align: 'center',
    })

    this.txtNext = this.add.text(PANEL_X, 160, 'NEXT', {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#888899',
    })

    this.txtChain = this.add.text(BX + (BOARD_COLS * BS) / 2, BY + (BOARD_ROWS * BS) / 2, '', {
      fontFamily: 'monospace',
      fontSize: '48px',
      color: '#FFE566',
      stroke: '#000000',
      strokeThickness: 6,
      align: 'center',
    }).setOrigin(0.5)

    this.txtState = this.add.text(PANEL_X, 400, '', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#888899',
    })

    this.txtGarbage = this.add.text(PANEL_X, 340, 'OUTGOING\n0', {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: '#FF6B6B',
      align: 'center',
    })

    const touchActive = loadSettings().touchControls === 'on' || (loadSettings().touchControls === 'auto' && isTouchDevice())
    this.txtHelp = this.add.text(PANEL_X, 500,
      touchActive
        ? 'Pan: Move\n2-finger: Rotate\nSwipe ↓: Drop\nTap: Hard Drop'
        : '← → Move\n↑ / X  Rotate CW\nZ      Rotate CCW\n↓      Soft Drop\nSPACE  Hard Drop\nR      Restart',
      {
        fontFamily: 'monospace',
        fontSize: '13px',
        color: '#555577',
        lineSpacing: 4,
      }
    )

    // Unlock AudioContext on first interaction
    this.input.keyboard!.once('keydown', () => this.sound.unlock())
    this.input.once('pointerdown', () => this.sound.unlock())

    // ── Key bindings ──────────────────────────────────────────────────────────
    this.keyLeft  = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT)
    this.keyRight = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT)
    this.keyDown  = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN)

    // One-shot keys via event
    this.input.keyboard!.on('keydown-UP',    () => this.logic.rotateCW())
    this.input.keyboard!.on('keydown-X',     () => this.logic.rotateCW())
    this.input.keyboard!.on('keydown-Z',     () => this.logic.rotateCCW())
    this.input.keyboard!.on('keydown-SPACE', () => this.logic.hardDrop())
    this.input.keyboard!.on('keydown-R',     () => this.restart())

    this.input.keyboard!.on('keydown-DOWN', () => this.logic.softDropStart())
    this.input.keyboard!.on('keyup-DOWN',   () => this.logic.softDropEnd())
    this.input.keyboard!.on('keydown-S',    () => this.logic.softDropStart())
    this.input.keyboard!.on('keyup-S',      () => this.logic.softDropEnd())

    // Initial left/right: first press moves 1, then DAS kicks in
    this.input.keyboard!.on('keydown-LEFT', () => {
      this.logic.moveLeft()
      this.dasTimer  = 0
      this.dasActive = false
    })
    this.input.keyboard!.on('keydown-RIGHT', () => {
      this.logic.moveRight()
      this.dasTimer  = 0
      this.dasActive = false
    })
    this.input.keyboard!.on('keyup-LEFT',  () => { this.dasTimer = 0; this.dasActive = false })
    this.input.keyboard!.on('keyup-RIGHT', () => { this.dasTimer = 0; this.dasActive = false })

    // ── Touch controls ─────────────────────────────────────────────────────
    const ts = loadSettings().touchControls
    const useTouch = ts === 'on' || (ts === 'auto' && isTouchDevice())
    if (useTouch) {
      this.touch = new TouchController(this, {
        moveLeft:      () => this.logic.moveLeft(),
        moveRight:     () => this.logic.moveRight(),
        rotateCW:      () => this.logic.rotateCW(),
        rotateCCW:     () => this.logic.rotateCCW(),
        hardDrop:      () => this.logic.hardDrop(),
        softDropStart: () => this.logic.softDropStart(),
        softDropEnd:   () => this.logic.softDropEnd(),
      })
    }

    this.events.on('shutdown', () => {
      this.input.keyboard!.removeAllListeners()
      this.touch?.destroy()
    })
  }

  update(_time: number, delta: number): void {
    const dt = delta / 1000

    // DAS
    if (this.keyLeft.isDown || this.keyRight.isDown) {
      this.dasTimer += dt
      if (!this.dasActive && this.dasTimer >= DAS_DELAY) {
        this.dasActive  = true
        this.dasRepeat  = 0
      }
      if (this.dasActive) {
        this.dasRepeat += dt
        if (this.dasRepeat >= DAS_REPEAT) {
          this.dasRepeat = 0
          if (this.keyLeft.isDown)  this.logic.moveLeft()
          if (this.keyRight.isDown) this.logic.moveRight()
        }
      }
    }

    this.logic.update(dt)

    // Reset touch gesture when a new piece spawns
    if (this.touch && this.logic.spawnCount !== this.lastSpawnCount) {
      this.lastSpawnCount = this.logic.spawnCount
      this.touch.resetGesture()
    }

    // ── Sound events ────────────────────────────────────────────────────────
    const phase = this.logic.phase.kind
    if (phase === 'chain_flash') {
      this.savedFlashCount = Math.max(this.savedFlashCount, this.logic.flashingCells.size)
    }
    if (this.prevPhase === 'chain_flash' && phase !== 'chain_flash') {
      this.sound.playPop(Math.max(1, Math.ceil(this.savedFlashCount / 4)))
      this.savedFlashCount = 0
    }
    if (this.logic.chainJustEnded) {
      this.sound.playChain(this.logic.lastChainCount)
      this.logic.chainJustEnded = false
    }
    if (this.prevPhase !== 'game_over' && phase === 'game_over' && !this.gameOverPlayed) {
      this.gameOverPlayed = true
      this.sound.playLose()
    }
    this.prevPhase = phase
    // ────────────────────────────────────────────────────────────────────────

    this.render(_time)
    this.updateHUD()
  }

  // ─── Rendering ─────────────────────────────────────────────────────────────

  private render(time: number): void {
    this.gfx.clear()
    this.drawBoardBackground()
    this.drawBoardCells(time)
    this.drawGhostPiece()
    this.drawCurrentPiece()
    this.drawNextPiece()
    this.drawBoardBorder()
  }

  private drawBoardBackground(): void {
    this.gfx.fillStyle(0x12122a)
    this.gfx.fillRect(BX, BY, BOARD_COLS * BS, BOARD_ROWS * BS)

    // Subtle grid lines
    this.gfx.lineStyle(1, 0x1e1e42, 0.6)
    for (let c = 1; c < BOARD_COLS; c++) {
      this.gfx.lineBetween(BX + c * BS, BY, BX + c * BS, BY + BOARD_ROWS * BS)
    }
    for (let r = 1; r < BOARD_ROWS; r++) {
      this.gfx.lineBetween(BX, BY + r * BS, BX + BOARD_COLS * BS, BY + r * BS)
    }
  }

  private drawBoardBorder(): void {
    this.gfx.lineStyle(2, 0x3a3a7a, 1)
    this.gfx.strokeRect(BX, BY, BOARD_COLS * BS, BOARD_ROWS * BS)
  }

  private drawBoardCells(time: number): void {
    const flashing    = this.logic.flashingCells
    const isFlash     = this.logic.phase.kind === 'chain_flash'
    const flashOn     = isFlash ? (Math.floor(time / 80) % 2 === 0) : false

    for (let row = 0; row < BOARD_ROWS; row++) {
      for (let col = 0; col < BOARD_COLS; col++) {
        const val = this.logic.board.get(col, row)
        if (val === CELL_EMPTY) continue

        const key     = `${col},${row}`
        const isGlow  = isFlash && flashing.has(key)
        const alpha   = isGlow && !flashOn ? 0.25 : 1.0
        const color   = isGlow && flashOn  ? 0xffffff : BLOCK_COLOR[val]
        const dark    = isGlow && flashOn  ? 0xcccccc : BLOCK_DARK[val]

        this.drawBlock(col, row, color, dark, alpha)
      }
    }
  }

  private drawGhostPiece(): void {
    if (this.logic.phase.kind !== 'falling') return

    const ghostRow = this.logic.ghostRow
    if (ghostRow === this.logic.current.row) return  // sitting on the piece itself

    const ghost: PieceState = { ...this.logic.current, row: ghostRow }
    const [[c1, r1], [c2, r2]] = getPiecePositions(ghost)

    this.drawBlockAlpha(c1, r1, BLOCK_COLOR[ghost.color1], 0.20)
    this.drawBlockAlpha(c2, r2, BLOCK_COLOR[ghost.color2], 0.20)
  }

  private drawCurrentPiece(): void {
    if (this.logic.phase.kind !== 'falling') return

    const [[c1, r1], [c2, r2]] = getPiecePositions(this.logic.current)
    this.drawBlock(c1, r1, BLOCK_COLOR[this.logic.current.color1], BLOCK_DARK[this.logic.current.color1])
    this.drawBlock(c2, r2, BLOCK_COLOR[this.logic.current.color2], BLOCK_DARK[this.logic.current.color2])
  }

  private drawNextPiece(): void {
    const np  = this.logic.nextPiece
    const nx  = PANEL_X
    const ny  = 185

    // Background box
    const boxW = BS * 2 + 16
    const boxH = BS * 2 + 16
    this.gfx.fillStyle(0x12122a)
    this.gfx.fillRect(nx, ny, boxW, boxH)
    this.gfx.lineStyle(1, 0x3a3a7a)
    this.gfx.strokeRect(nx, ny, boxW, boxH)

    // Draw the pair: color2 on top, color1 below
    const cx = Math.floor(nx + boxW / 2 - BS / 2)
    this.drawBlockAt(cx, ny + 8,        np.color2, BLOCK_DARK[np.color2])   // sub (top)
    this.drawBlockAt(cx, ny + 8 + BS,   np.color1, BLOCK_DARK[np.color1])   // main (bottom)
  }

  // ─── Block draw helpers ────────────────────────────────────────────────────

  private drawBlock(col: number, row: number, color: number, dark: number, alpha = 1): void {
    const x = BX + col * BS
    const y = BY + row * BS
    this.drawBlockAt(x, y, color, dark, alpha)
  }

  private drawBlockAlpha(col: number, row: number, color: number, alpha: number): void {
    const x = BX + col * BS
    const y = BY + row * BS
    this.gfx.fillStyle(color, alpha)
    this.gfx.fillRoundedRect(x + 2, y + 2, BS - 4, BS - 4, 5)
  }

  private drawBlockAt(x: number, y: number, color: number, dark: number, alpha = 1): void {
    const p = 2  // padding

    // Main fill
    this.gfx.fillStyle(color, alpha)
    this.gfx.fillRoundedRect(x + p, y + p, BS - p * 2, BS - p * 2, 5)

    // Top highlight (lighter band)
    this.gfx.fillStyle(0xffffff, 0.22 * alpha)
    this.gfx.fillRect(x + p + 2, y + p + 2, BS - p * 2 - 4, Math.floor((BS - p * 2) * 0.40))

    // Bottom shadow
    this.gfx.fillStyle(dark, 0.35 * alpha)
    this.gfx.fillRect(x + p + 2, y + BS - p - 6, BS - p * 2 - 4, 5)
  }

  // ─── HUD ───────────────────────────────────────────────────────────────────

  private updateHUD(): void {
    this.txtScore.setText(`SCORE\n${this.logic.score.toLocaleString()}`)

    // Chain display
    if (this.logic.chainDisplayTimer > 0) {
      const count = this.logic.lastChainCount
      const alpha = Math.min(1, this.logic.chainDisplayTimer / 0.5)
      this.txtChain
        .setText(`×${count}\nCHAIN!`)
        .setAlpha(alpha)
        .setVisible(true)
    } else if (this.logic.currentChainCount >= 2) {
      this.txtChain
        .setText(`×${this.logic.currentChainCount}`)
        .setAlpha(1)
        .setVisible(true)
    } else {
      this.txtChain.setVisible(false)
    }

    // Outgoing garbage (for multiplayer preview)
    if (this.logic.outgoingGarbage > 0) {
      this.txtGarbage.setText(`OUTGOING\n${this.logic.outgoingGarbage} 💣`).setVisible(true)
    } else {
      this.txtGarbage.setVisible(false)
    }

    // Phase / game-over overlay
    if (this.logic.phase.kind === 'game_over') {
      this.txtState.setText('GAME OVER\nPress R to restart')
        .setStyle({ color: '#FF6B6B', fontSize: '22px' })
    } else {
      this.txtState.setText('')
    }
  }

  // ─── Restart ───────────────────────────────────────────────────────────────

  private restart(): void {
    this.logic = new GameLogic(Date.now() | 0, SPEEDS.normal)
    this.dasTimer        = 0
    this.dasActive       = false
    this.prevPhase       = ''
    this.savedFlashCount = 0
    this.gameOverPlayed  = false
    this.txtChain.setVisible(false)

    // Re-bind touch controller to new logic instance
    if (this.touch) {
      this.touch.destroy()
      this.touch = new TouchController(this, {
        moveLeft:      () => this.logic.moveLeft(),
        moveRight:     () => this.logic.moveRight(),
        rotateCW:      () => this.logic.rotateCW(),
        rotateCCW:     () => this.logic.rotateCCW(),
        hardDrop:      () => this.logic.hardDrop(),
        softDropStart: () => this.logic.softDropStart(),
        softDropEnd:   () => this.logic.softDropEnd(),
      })
    }
  }
}
